type AppEnv = "local" | "dev" | "hmg" | "prd";

function optionalEnv(name: string): string {
  return String((process.env as any)?.[name] ?? "").trim();
}

function sanitizeUrl(raw: string): string {
  return String(raw || "")
    .trim()
    .replace(/\s+/g, "");
}

function requireValidUrlFromEnv(name: string): string {
  const value = sanitizeUrl(optionalEnv(name));

  if (!value) {
    throw new Error(`[ENV] Variável obrigatória não encontrada: ${name}`);
  }

  try {
    const url = new URL(value);
    if (!/^https?:$/.test(url.protocol)) throw new Error("protocol inválido");
  } catch {
    throw new Error(`[ENV] ${name} não é uma URL válida: "${value}"`);
  }

  return value;
}

function getHostInfo(): { host: string; hostname: string } {
  if (typeof window === "undefined") return { host: "", hostname: "" };
  return {
    host: String(window.location.host || "").toLowerCase(),
    hostname: String(window.location.hostname || "").toLowerCase(),
  };
}

function isLocalhostHost(): boolean {
  const { hostname } = getHostInfo();
  return hostname === "localhost" || hostname === "127.0.0.1";
}

function normalizeAppEnv(raw: string): AppEnv | null {
  const v = String(raw || "").trim();
  if (!v) return null;

  const lower = v.toLowerCase();

  if (lower === "local") return "local";
  if (lower === "dev") return "dev";
  if (lower === "hmg") return "hmg";
  if (lower === "prd") return "prd";

  if (lower === "development") return "dev";
  if (lower === "staging") return "hmg";
  if (lower === "production") return "prd";

  return null;
}

function forcedEnvFromFrontHost(): AppEnv | null {
  const { host, hostname } = getHostInfo();

  if (hostname === "localhost" || hostname === "127.0.0.1") return "local";

  if (host === "dev-meritocracia.fiemg.com.br") return "dev";

  if (host === "hmg-meriticracia.fiemg.com.br") return "hmg";

  if (host === "meritocracia.fiemg.com.br") return "prd";

  return null;
}

function inferEnvFromHost(): AppEnv {
  const forced = forcedEnvFromFrontHost();
  if (forced) return forced;

  return "prd";
}

function assertEnvMatchesUrl(appEnv: AppEnv, baseUrl: string) {
  if (appEnv === "local") {
    if (isLocalhostHost()) return;

    throw new Error(
      `[ENV] Ambiente inconsistente!\n` +
        `APP_ENV final="local" não é permitido fora do localhost.\n` +
        `Host atual: "${typeof window !== "undefined" ? window.location.host : "unknown"}"\n` +
        `REACT_APP_ENV (build)="${optionalEnv("REACT_APP_ENV")}"\n` +
        `REACT_APP_API_BASE_URL="${baseUrl}"`,
    );
  }

  const lower = baseUrl.toLowerCase();

  const expectedToken: Record<Exclude<AppEnv, "local">, string> = {
    dev: "meritocracia-dev",
    hmg: "meritocracia-hmg",
    prd: "meritocracia-prd",
  };

  const token = expectedToken[appEnv];

  if (!lower.includes(token)) {
    throw new Error(
      `[ENV] Ambiente inconsistente!\n` +
        `APP_ENV final="${appEnv}"\n` +
        `REACT_APP_ENV (build)="${optionalEnv("REACT_APP_ENV")}"\n` +
        `REACT_APP_API_BASE_URL="${baseUrl}"\n` +
        `Esperado conter: "${token}"`,
    );
  }
}

export const ENV = (() => {
  const forced = forcedEnvFromFrontHost();

  const rawFromEnv = optionalEnv("REACT_APP_ENV");
  const fromEnv = normalizeAppEnv(rawFromEnv);

  const APP_ENV: AppEnv = forced || fromEnv || inferEnvFromHost();

  const API_BASE_URL = requireValidUrlFromEnv("REACT_APP_API_BASE_URL");

  assertEnvMatchesUrl(APP_ENV, API_BASE_URL);

  const SYSTEM_USER = optionalEnv("SYSTEM_USER");
  const SYSTEM_PASSWORD = optionalEnv("SYSTEM_PASSWORD");

  return { APP_ENV, API_BASE_URL, SYSTEM_USER, SYSTEM_PASSWORD };
})();
